import { apiBaseUrl, apiFetch, getAccessToken } from './client'

// A team's contact is a person on it, by a method they verified themselves:
// a Discord account linked through Connections, their verified email, or a
// phone they verified by text and chose to share. These are the signed-in
// person's own calls for that — reading what they have, sharing a phone, and
// linking Discord without leaving the form they're in.

// carousel: the only method for a player under 18 — leagues message them
// through Carousel and never see an address.
export type ContactMethod = 'discord' | 'phone' | 'email' | 'carousel'

/** One team or event someone is the contact for. */
export interface ContactUse {
  kind: 'primary' | 'event'
  team_id: string
  team_name: string
  org_slug: string
  method: ContactMethod
  /** The event's name, for kind 'event'. */
  competition?: string
}

/** What the signed-in person can be reached by, as /auth/me reports it. */
export interface MyContactMethods {
  discord?: string
  email?: string
  /** Last four of the verified phone. */
  phone_last4?: string
  phone_shared: boolean
  /** A verified phone is on the account but not shared yet. */
  phone_shareable: boolean
  /** Methods usable as a team contact, in the order to offer them. */
  available: ContactMethod[]
  /** What each method is the contact for — what withdrawing it would affect. */
  used_by?: ContactUse[]
}

export async function getMyContactMethods(): Promise<MyContactMethods> {
  const me = await apiFetch<{ contact_methods?: MyContactMethods }>('/auth/me')
  return me.contact_methods ?? { phone_shared: false, phone_shareable: false, available: [] }
}

/** Texts a code to share a phone. It must be the phone verified on the account, if there is one. */
export async function sendContactPhoneCode(phone: string): Promise<void> {
  await apiFetch('/auth/phone/send', { method: 'POST', body: JSON.stringify({ phone_number: phone, purpose: 'contact' }) })
}

/** Checks the code and shares the phone, encrypted, as a contact method. */
export async function verifyAndShareContactPhone(phone: string, code: string): Promise<void> {
  await apiFetch('/auth/phone/verify', {
    method: 'POST',
    body: JSON.stringify({ phone_number: phone, code, share_for_contact: true }),
  })
}

/** Stops sharing the phone. Refused (409, naming the team) while a team relies on it. */
export async function stopSharingContactPhone(): Promise<void> {
  await apiFetch('/auth/contact-phone', { method: 'DELETE' })
}

/**
 * Links Discord in a popup, so the form underneath keeps everything typed.
 * The OAuth round trip lands the popup back on this page; once it's on our own
 * origin again it's closed and this resolves. Also resolves if the person
 * closes the popup — callers re-read the methods either way.
 */
export function linkDiscordInPopup(win: Window = window): Promise<void> {
  const token = getAccessToken() ?? ''
  // Keep the page's own query (e.g. ?season=…&action=register) so a
  // full-page fallback lands back on the same form.
  const params = new URLSearchParams(win.location.search)
  params.set('linked', 'discord')
  const returnTo = encodeURIComponent(`${win.location.origin}${win.location.pathname}?${params.toString()}`)
  const url = `${apiBaseUrl()}/auth/oauth/discord?token=${encodeURIComponent(token)}&return_to=${returnTo}`
  const popup = win.open(url, 'carousel-link-discord', 'width=520,height=720')
  return new Promise(resolve => {
    if (!popup) {
      // Popups blocked: fall back to a full-page link.
      win.location.href = url
      resolve()
      return
    }
    const timer = win.setInterval(() => {
      let home = false
      try {
        home = popup.location.origin === win.location.origin
      } catch {
        // Still on Discord's or the API's origin.
      }
      if (home || popup.closed) {
        win.clearInterval(timer)
        if (!popup.closed) popup.close()
        resolve()
      }
    }, 500)
  })
}

/** What each team or event relies on one method for, as a line to show. */
export function describeContactUse(use: ContactUse): string {
  return use.kind === 'event' && use.competition
    ? `${use.competition} — ${use.team_name} (event contact)`
    : `${use.team_name} (${use.kind === 'event' ? 'event' : 'primary'} contact)`
}

/** The teams and events that rely on one method — what withdrawing it would affect. */
export function usesOfMethod(methods: MyContactMethods | null | undefined, method: ContactMethod): ContactUse[] {
  return (methods?.used_by ?? []).filter(u => u.method === method)
}

// ─── Messages through Carousel ──────────────────────────────────────────────
// A league or club reaches a team's contact — always, for a player under 18 —
// by a message on Carousel. The recipient replies in the same thread.

export interface ContactMessage {
  id: string
  sender_id: string
  sender_name: string
  body: string
  created_at: string
  /** Sent by the signed-in person. */
  mine: boolean
}

export interface ContactThread {
  id: string
  team_id: string
  team_name: string
  recipient_name: string
  messages: ContactMessage[]
}

/** One conversation. 404 when it doesn't exist or isn't the caller's. */
export function getContactThread(threadId: string): Promise<ContactThread> {
  return apiFetch<ContactThread>(`/contact-messages/${encodeURIComponent(threadId)}`)
}

export function replyToContactThread(threadId: string, body: string): Promise<ContactThread> {
  return apiFetch<ContactThread>(`/contact-messages/${encodeURIComponent(threadId)}/replies`, {
    method: 'POST',
    body: JSON.stringify({ body }),
  })
}

/** Messages a team's primary contact, or the contact for one registration. */
export function sendContactMessage(teamId: string, body: string, registrationId?: string): Promise<ContactThread> {
  return apiFetch<ContactThread>(`/teams/${encodeURIComponent(teamId)}/contact-messages`, {
    method: 'POST',
    body: JSON.stringify(registrationId ? { body, registration_id: registrationId } : { body }),
  })
}
